import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { CommonHeader, DataTable } from "../../../components";
import useDebounce from "../../../hooks/useDebounce";
import {
  AddEditAdminCityModal,
  DeleteConfirmationModal,
} from "../../../modals";

import {
  deleteCity_V2,
  getAllCities_V2,
} from "../../../Services_V2/City.service";
import { BasePayload, ICity } from "../../../types";
import { showError } from "../../../utils/error";
import { mobileText } from "../../../utils/responsiveStyle";
import styles from "./city.module.css";
import { getColumns } from "./column";

const CitiesPage = () => {
  // Add Edit City Modal
  const [openCity, setOpenCity] = useState(false);
  const [adminCities, setAdminCities] = useState<ICity[]>([]);

  // Paginzation
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  // Search
  const [searchTerm, setSearchTerm] = useState("");

  // Edit
  const [editableCity, setEditableCity] = useState<ICity | null>(null);

  // Delete
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletableCity, setDeletableCity] = useState<ICity | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getPayload = useCallback((): BasePayload => {
    return {
      search: debouncedSearchTerm,
      page: currentPage + 1,
      limit: rowsPerPage,
    };
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm]);

  const fetchAdminCities = useCallback(async () => {
    try {
      const payload = getPayload();
      const res = await getAllCities_V2(payload);
      setAdminCities(res.data || []);
      setTotal(res?.total || 0);
    } catch (error) {}
  }, [getPayload]);

  useEffect(() => {
    fetchAdminCities();
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  const refetch = useCallback(() => {
    fetchAdminCities();
  }, [fetchAdminCities]);

  const handleDelete = useCallback(async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteCity_V2(deletableCity?._id as string);
      toast.success(res?.message);
      setDeletableCity(null);
      setOpenDelete(false);
      refetch();
    } catch (error) {
      showError(error);
    } finally {
      setDeleteLoading(false);
    }
  }, [deletableCity, refetch]);

  const columns = getColumns({ currentPage, rowsPerPage, data: adminCities });

  return (
    <div className={styles.container}>
      <CommonHeader
        title={`Total ${total} Cities`}
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        onAdd={() => {
          setEditableCity(null);
          setOpenCity(true);
        }}
        addLabel="Add New City"
      />
      <Box sx={{ mt: 4 }}>
        <DataTable
          data={adminCities}
          columns={columns}
          total={total}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          showPagination
          onPageChange={(_, page) => setCurrentPage(page)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setCurrentPage(0);
          }}
          onEdit={(row) => {
            setEditableCity(row);
            setOpenCity(true);
          }}
          onDelete={(row) => {
            setDeletableCity(row);
            setOpenDelete(true);
          }}
          tableCellSx={{
            py: 0.3,
            ...mobileText,
            lineHeight: 1.2,
          }}
        />
      </Box>

      <AddEditAdminCityModal
        open={openCity}
        onClose={() => setOpenCity(false)}
        refetch={refetch}
        editableCity={editableCity}
      />
      <DeleteConfirmationModal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default CitiesPage;
